import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * Avaliações da landing: nota de 1 a 5 + comentário, enviadas por usuários
 * logados e publicadas só depois de aprovadas.
 *
 * Dois caminhos de dado, de propósito:
 *
 * - ESCRITA e leitura da própria avaliação vão em `reviews/{uid}` — o id do
 *   documento é o uid, então uma conta tem no máximo uma avaliação e o autor
 *   relê a dele com um `get` direto.
 * - LEITURA PÚBLICA vem de `publicStats/reviews`, um agregado montado no
 *   servidor pelo trigger `rebuildPublicReviews` só com o que foi aprovado.
 *   A landing nunca toca em `reviews`: se tocasse, o visitante anônimo
 *   conseguiria ler a fila de pendentes e reprovadas.
 */

export const REVIEW_COMMENT_MAX = 1500;
export const REVIEW_COMMENT_MIN = 3;

export type ReviewStatus = "pending" | "approved" | "rejected";

/** A avaliação como o autor a vê (documento `reviews/{uid}`). */
export interface MyReview {
  rating: number;
  comment: string;
  authorName: string;
  churchName: string;
  role: string;
  status: ReviewStatus;
  createdAt: Date | null;
  updatedAt: Date | null;
}

/** Um comentário aprovado, como aparece no modal da landing. */
export interface PublicReviewItem {
  id: string;
  rating: number;
  comment: string;
  authorName: string;
  churchName: string;
  role: string;
  initials: string;
  /** ISO 8601, ou null se a avaliação foi gravada sem data. */
  createdAt: string | null;
}

export interface PublicReviewsSummary {
  total: number;
  /** Média com uma casa decimal. 0 quando não há avaliação aprovada. */
  average: number;
  /** Quantas avaliações em cada nota, para as barras do modal. */
  distribution: Record<1 | 2 | 3 | 4 | 5, number>;
  items: PublicReviewItem[];
}

const RESUMO_VAZIO: PublicReviewsSummary = {
  total: 0,
  average: 0,
  distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  items: [],
};

function paraData(valor: unknown): Date | null {
  if (valor instanceof Timestamp) return valor.toDate();
  if (valor instanceof Date) return valor;
  return null;
}

/**
 * Resumo público das avaliações. Uma leitura de um documento.
 *
 * Cache em memória por módulo porque a home monta o selo no Hero e o bloco em
 * Depoimentos: dois componentes irmãos que pediriam o mesmo documento na mesma
 * renderização. O cache guarda a *promise*, não o resultado, para que chamadas
 * simultâneas compartilhem o mesmo request em vez de disparar dois.
 */
let resumoEmCache: Promise<PublicReviewsSummary> | null = null;

export function getPublicReviews(): Promise<PublicReviewsSummary> {
  if (!resumoEmCache) {
    resumoEmCache = buscarResumo().catch((erro) => {
      // Falha de rede não pode virar cache permanente de erro: limpa para que
      // a próxima montagem tente de novo.
      resumoEmCache = null;
      throw erro;
    });
  }
  return resumoEmCache;
}

async function buscarResumo(): Promise<PublicReviewsSummary> {
  const snap = await getDoc(doc(db, "publicStats", "reviews"));
  if (!snap.exists()) return RESUMO_VAZIO;

  const d = snap.data() as Partial<PublicReviewsSummary>;
  const distribution = {
    1: Number(d.distribution?.[1]) || 0,
    2: Number(d.distribution?.[2]) || 0,
    3: Number(d.distribution?.[3]) || 0,
    4: Number(d.distribution?.[4]) || 0,
    5: Number(d.distribution?.[5]) || 0,
  };

  return {
    total: Number(d.total) || 0,
    average: Number(d.average) || 0,
    distribution,
    items: Array.isArray(d.items) ? (d.items as PublicReviewItem[]) : [],
  };
}

/** A avaliação do usuário logado, ou null se ele ainda não avaliou. */
export async function getMyReview(): Promise<MyReview | null> {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;

  const snap = await getDoc(doc(db, "reviews", uid));
  if (!snap.exists()) return null;

  const d = snap.data();
  return {
    rating: Number(d.rating) || 0,
    comment: String(d.comment || ""),
    authorName: String(d.authorName || ""),
    churchName: String(d.churchName || ""),
    role: String(d.role || ""),
    status: (d.status as ReviewStatus) || "pending",
    createdAt: paraData(d.createdAt),
    updatedAt: paraData(d.updatedAt),
  };
}

export interface SubmitReviewInput {
  rating: number;
  comment: string;
  authorName: string;
  churchId: string;
  churchName: string;
  role: string;
}

/**
 * Grava (ou reescreve) a avaliação do usuário logado.
 *
 * Sempre volta para `pending`: reescrever uma avaliação já aprovada é o
 * caminho óbvio para publicar texto sem moderação, e a regra do Firestore
 * também exige `status == 'pending'` na escrita do autor.
 *
 * `merge: true` preserva `createdAt` na reescrita — o histórico de quando a
 * pessoa avaliou pela primeira vez é o que dá idade à prova social.
 */
export async function submitReview(input: SubmitReviewInput): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Você precisa estar logado para avaliar.");

  const rating = Math.round(Number(input.rating));
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
    throw new Error("Escolha uma nota de 1 a 5 estrelas.");
  }

  const comment = input.comment.trim();
  if (comment.length < REVIEW_COMMENT_MIN) {
    throw new Error("Escreva um comentário sobre sua experiência.");
  }
  if (comment.length > REVIEW_COMMENT_MAX) {
    throw new Error(
      `O comentário passa de ${REVIEW_COMMENT_MAX} caracteres. Resuma um pouco.`,
    );
  }

  const ref = doc(db, "reviews", user.uid);
  const jaExiste = (await getDoc(ref)).exists();

  await setDoc(
    ref,
    {
      uid: user.uid,
      rating,
      comment,
      // A regra limita `authorName` a 120 caracteres; corta aqui para que um
      // displayName absurdo dê erro de validação amigável em vez de
      // permission-denied.
      authorName: input.authorName.trim().slice(0, 120),
      churchId: input.churchId,
      churchName: input.churchName.trim().slice(0, 160),
      role: input.role.trim().slice(0, 80),
      // Guardado para o admin conseguir responder/apurar; nunca vai para o
      // agregado público.
      contactEmail: user.email || "",
      status: "pending" as ReviewStatus,
      ...(jaExiste ? {} : { createdAt: serverTimestamp() }),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}
